import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ensureNamespaceAsync } from '../../i18n/ensurePageI18n';

// 圖片導入 (請替換為您的實際切圖)
// 建議將 PDF 中的圖片切出並命名如下
import wscanMachine from './img/measurement.png'; // 那台大的機器
import appPi from './img/piFilm .png';
import appCopper from './img/copperFoil.png';
import appPmma from './img/pmma.png';
import appFilm from './img/thinFilm.png';
import appNonwoven from './img/nonwovenFabric.png';
import appRaw from './img/rawFoil.png';
import appFiber from './img/fiberglassCloth.png';
import appSemi from './img/semiCuredSheet.png';

// 臨時佔位圖 (若無圖片時防止報錯)
import placeholderImg from './img/zh.jpg';

// 新增：導入測厚介紹影片
import measurementVideo from '../../assets/video/measurementVideo.mp4';

const bundles = {
  en: {
    measurement: {
      page_title: "Thickness Measurement System",
      page_sub: "",
      features: {
        o_frame: {
          title: "Introduction to O-Type Frame",
          list: [
            "The scanning frame is made from a single bent steel plate,which is resistant to deformation. It features a fully enclosed design that effectively prevents dust from entering the thickness gauge."
          ]
        },
        x_ray: {
          title: "Introduction to X-Ray Radiation",
          list: [
            "Probe: Uses low-energy X-rays, with core components independently developed that are free from radiationpollution, environmentally exempt,and have a long lifespan.",
            "Measurement Range: 3μm - 5mm",
            "Static Resolution: 0.05μm",
            "Dynamic Measurement Accuracy: ±0.1μm or ± 0.1%",
          ]
        },
        motion: {
          title: "Motion Unit",
          list: [
            "Uses high-precision servo-driven motors,with high positioning accuracy, good repeatability, and closed-loop control functionality.",
            "Uses high-speed network communication to ensure that the scanning paths of multiple thickness gauges are completely consistent.",
            "Measurement Response Speed: 10ms",
            "Ambient Temperature Range: 0 - 50°C"
          ]
        },
        station: {
          title: "Introduction to the Operating Station",
          list: [
            "Operating System: Windows 10 Pro The unique X-ray thickness gauge differential algorithm accurately calculates the areal density of the net coating amount.",
            "Real-time online thickness analysis,multiple synthesis of trend curve target values, standard value upper and lower limit alarm model, and alignment settings analysis.",
            "Formula Settings: Target Value, Maximum Value, Minimum Value, Average Value,Standard Deviation, Upper and Lower Limit Values, and Die Alignment Settings.",
            "Reports:<br/>Over-limit Alarm, Thickness Work Order Report."
          ]
        }
      },
      applications: {
        title: "Industry Applications",
        items: [
          "PI Film", "Copper Foil", "PMMA", "Thin Film",
          "Nonwoven Fabric", "Raw Foil", "Fiberglass Cloth", "Semi-cured Sheet"
        ]
      }
    }
  },
  zh: {
    measurement: {
      page_title: "測厚系統",
      page_sub: "Thickness Measurement System",
      features: {
        o_frame: {
          title: "O 型機架介紹",
          list: [
            "掃描架採用鋼板一體折彎而成不易變形，全封閉式設計有效防止粉塵進入測厚儀",
          ]
        },
        x_ray: {
          title: "X-Ray 射線介紹",
          list: [
            "探頭：採用低能量的X 射線，自主研發的核心部件無輻射污染、環保豁免、壽命長。",
            "測量範圍 3um - 5mm",
            "靜止解析度 0.05um",
            "動態測量精度 ±0.1um 或 ± 0.1%"
          ]
        },
        motion: {
          title: "運動單元",
          list: [
            "採用高精度伺服驅動電機，定位精度高，重複性好，閉環控制功能。",
            "採用高速網路通訊，確保幾台測厚儀的掃描路徑完全一致。",
            "測量回應速度 10ms",
            "環境溫度範圍 0 - 50°C"
          ]
        },
        station: {
          title: "操作站介紹",
          list: [
            "作業系統 WIN10 PRO",
            "獨特 X 射線測厚儀的差值演算法，精準將淨塗布量的面密度計算出來。",
            "在線實時厚度分析，多次合成趨勢曲線目標值，標準值上下限報警模頭對位設定分析",
            "配方設定：<br/>目標值，最大值，最小值，平均值，標準差，上下限值，模頭對位設定",
            "報表：<br/>超限值報警，厚度工單報表"
          ]
        }
      },
      applications: {
        title: "產業應用實例",
        items: [
          "PI 膜", "銅箔", "PMMA", "薄膜",
          "不織布", "生箔", "玻纖布", "半固化片"
        ]
      }
    }
  }
};

// 安全列表轉換函數
const safeList = (tFunc, key) => {
  const raw = tFunc(key, { returnObjects: true });
  if (Array.isArray(raw)) return raw;
  if (raw == null) return [];
  if (typeof raw === 'object') return Object.values(raw);
  return [raw];
};

const Measurement = () => {
  const { t, i18n } = useTranslation('measurement');
  const [ready, setReady] = useState(false);

  // 新增：影片參考與封面控制
  const videoRef = useRef(null);
  const [thumbSet, setThumbSet] = useState(false);

  useEffect(() => {
    let mounted = true;
    ensureNamespaceAsync(i18n, 'measurement', bundles).then(() => { if (mounted) setReady(true); });
    return () => { mounted = false; };
  }, [i18n]);

  // 當影片中繼資料載入後，跳到第 1 秒
  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v || thumbSet) return;
    try {
      v.currentTime = 1; // 跳到第 1 秒
    } catch (e) {
      console.error('Failed to seek video:', e);
    }
  };

  // 當 seek 完成後暫停，顯示 1 秒畫面作為封面
  const handleSeeked = () => {
    const v = videoRef.current;
    if (!v || thumbSet) return;
    try {
      v.pause();
      setThumbSet(true);
    } catch (e) {
      console.error('Failed to pause video:', e);
    }
  };

  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center bg-black text-gray-300">Loading…</div>;
  }

  const currentLang = i18n.language || 'zh';

  // 應用圖片陣列 (對應 8 個項目)
  // 如果您還沒切好圖，這裡會暫時使用 placeholderImg
  const appImages = [appPi, appCopper, appPmma, appFilm, appNonwoven, appRaw, appFiber, appSemi].map(img => img || placeholderImg);

  // 機器主圖
  const mainMachine = wscanMachine || placeholderImg;

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-8 font-sans bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white">

      {/* 返回按鈕 */}
      <div className="w-full max-w-6xl relative mb-8">
        <Link to="/" className="inline-block px-5 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white font-medium transition shadow-lg">
          ← {currentLang.startsWith('zh') ? '返回' : 'Back'}
        </Link>
      </div>

      {/* 白色主內容卡片 (Light Theme Wrapper) */}
      <div className="max-w-6xl w-full bg-white shadow-2xl rounded-xl overflow-hidden text-gray-800">

        {/* ================= HEADER & MACHINE IMAGE ================= */}
        <section className="p-6 md:p-12 border-gray-200 relative">
          <div className="text-left mb-8 border-b pb-4 border-gray-200">
            <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">{t('measurement:measurement.page_title')}</h1>
            <h2 className="text-xl text-gray-600 font-medium">{t('measurement:measurement.page_sub')}</h2>
          </div>
        </section>

        {/* ================= CHART & FEATURES SECTION ================= */}
        <section className="p-6 md:p-12 bg-gray-50">
          {/* Machine Image */}
          <div className="flex justify-center items-center relative py-8">
            {/* 裝飾背景圓 */}
            <div className="absolute w-64 h-64 bg-blue-50 rounded-full filter blur-3xl opacity-50"></div>
            <img
              src={mainMachine}
              alt="Measurement Machine"
              className="relative z-10 w-full max-w-3xl object-contain hover:scale-105 transition duration-700"
            />
          </div>

          {/* 新增：測厚介紹影片（置於機器主圖下方） */}
          <div className="flex justify-center items-center relative pb-8">
            <div className="w-full max-w-3xl rounded-xl overflow-hidden shadow-xl border border-gray-200">
              <video
                ref={videoRef}
                src={measurementVideo}
                className="w-full h-auto bg-black"
                controls
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={handleLoadedMetadata}
                onSeeked={handleSeeked}
              >
                您的瀏覽器不支援 HTML5 影片。
              </video>
            </div>
          </div>

          {/* Desktop: SVG Chart Layout */}
          <div className="hidden lg:block relative w-full max-w-5xl mx-auto py-8 px-4">

            {/* SVG Lines Layer */}
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0" viewBox="0 0 800 700" preserveAspectRatio="xMidYMid meet">
              <defs>
                <marker id="dot" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6">
                  <circle cx="5" cy="5" r="5" fill="#2563EB" />
                </marker>
              </defs>
              {/* Central Donut (4 deep-blue quadrants unified color) */}
              <circle cx="400" cy="350" r="40" fill="#ffffff" />
              <path d="M 400 275 A 75 75 0 0 0 325 350 L 360 350 A 40 40 0 0 1 400 310 Z" fill="#1E3A8A" stroke="#ffffff" strokeWidth="3" />
              <path d="M 400 275 A 75 75 0 0 1 475 350 L 440 350 A 40 40 0 0 0 400 310 Z" fill="#1E3A8A" stroke="#ffffff" strokeWidth="3" />
              <path d="M 475 350 A 75 75 0 0 1 400 425 L 400 390 A 40 40 0 0 0 440 350 Z" fill="#1E3A8A" stroke="#ffffff" strokeWidth="3" />
              <path d="M 325 350 A 75 75 0 0 0 400 425 L 400 390 A 40 40 0 0 1 360 350 Z" fill="#1E3A8A" stroke="#ffffff" strokeWidth="3" />

              {/* Original connector paths (longer, not overlapping lists) */}
              <path d="M 350 330 L 300 250 L 250 250" fill="none" stroke="#2563EB" strokeWidth="2" markerEnd="url(#dot)" />
              <path d="M 450 330 L 500 200 L 550 200" fill="none" stroke="#2563EB" strokeWidth="2" markerEnd="url(#dot)" />
              <path d="M 350 370 L 300 450 L 250 450" fill="none" stroke="#2563EB" strokeWidth="2" markerEnd="url(#dot)" />
              <path d="M 450 370 L 500 500 L 550 500" fill="none" stroke="#2563EB" strokeWidth="2" markerEnd="url(#dot)" />
            </svg>

            {/* Feature blocks with custom bullet alignment */}
            <div className="absolute top-[150px] left-[0px] w-[280px] pr-4">
              <h4 className="text-base font-bold text-blue-800 mb-2">{t('measurement:measurement.features.o_frame.title')}</h4>
              <ul className="space-y-2">
                {safeList(t, 'measurement:measurement.features.o_frame.list').map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-700 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700 leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute top-[100px] right-[0px] w-[280px] pl-4">
              <h4 className="text-base font-bold text-blue-800 mb-2">{t('measurement:measurement.features.x_ray.title')}</h4>
              <ul className="space-y-2">
                {safeList(t, 'measurement:measurement.features.x_ray.list').map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-700 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700 leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute top-[380px] left-[0px] w-[280px] pr-4">
              <h4 className="text-base font-bold text-blue-800 mb-2">{t('measurement:measurement.features.motion.title')}</h4>
              <ul className="space-y-2">
                {safeList(t, 'measurement:measurement.features.motion.list').map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-700 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700 leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute top-[420px] right-[0px] w-[280px] pl-4">
              <h4 className="text-base font-bold text-blue-800 mb-2">{t('measurement:measurement.features.station.title')}</h4>
              <ul className="space-y-2">
                {safeList(t, 'measurement:measurement.features.station.list').map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-700 flex-shrink-0" />
                    <span className="ml-2 text-xs text-gray-700 leading-relaxed break-words" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>

            {/* Spacer to ensure enough height for bottom blocks */}
            <div className="h-[750px]"></div>
          </div>

          {/* Mobile: Stacked Card Layout */}
          <div className="lg:hidden grid grid-cols-1 gap-6">
            <MobileFeatureCard title={t('measurement:measurement.features.o_frame.title')} items={safeList(t, 'measurement:measurement.features.o_frame.list')} />
            <MobileFeatureCard title={t('measurement:measurement.features.x_ray.title')} items={safeList(t, 'measurement:measurement.features.x_ray.list')} />
            <MobileFeatureCard title={t('measurement:measurement.features.motion.title')} items={safeList(t, 'measurement:measurement.features.motion.list')} />
            <MobileFeatureCard title={t('measurement:measurement.features.station.title')} items={safeList(t, 'measurement:measurement.features.station.list')} />
          </div>
        </section>

        {/* ================= APPLICATIONS SECTION (Under Chart) ================= */}
        <section className="p-6 md:p-12 bg-white border-t border-gray-200">

          {/* Grid of 8 Images */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {safeList(t, 'measurement:measurement.applications.items').map((label, idx) => (
              <div key={idx} className="group relative rounded-lg overflow-hidden shadow-lg border border-gray-100">
                {/* Image Container */}
                <div className="aspect-[4/3] bg-gray-200 overflow-hidden relative">
                  <img
                    src={appImages[idx]}
                    alt={label}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-700 ease-in-out"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80"></div>
                </div>

                {/* Label */}
                <div className="absolute bottom-0 left-0 w-full p-3 text-center">
                  <span className="text-white text-sm md:text-base font-bold tracking-wide drop-shadow-md">
                    {label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>

      <div className="mt-8 text-gray-300 text-xs">© 2025 Wintriss Engineering. All rights reserved.</div>
    </div>
  );
};

// Helper Component for Mobile View
const MobileFeatureCard = ({ title, items }) => (
  <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-blue-600">
    <h4 className="text-lg font-bold text-blue-900 mb-3">{title}</h4>
    <ul className="space-y-2 pl-4 list-disc text-sm text-gray-700">
      {items.map((item, idx) => (
        <li key={idx} dangerouslySetInnerHTML={{ __html: item }} />
      ))}
    </ul>
  </div>
);

export default Measurement;

